import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import Color from '@styles/Color';

import Label from '@components/dataEntry/Input/Label';
import DropdownWrapper, {
  dropdownDirMap,
} from '@components/wrapper/DropdownWrapper';
import { AnimatePresence } from 'framer-motion';

import { Ihashtag } from '@typings/common/hashtag.interface';
import { useCollection } from '@nandorojo/swr-firestore';
import ResultDropdown from '../Input/TagInput/ResultDropdown';
import { filterTitleBySearchValue } from '@utils/filterBySearchValue';
import Avatar from '@components/dataDisplay/Avatar';
import TagItem from './TagItem';
import LabelSet from '../Input/Label/LabelSet';
import { useRecoilValue } from 'recoil';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import { CollectionPath } from '@typings/collections.enum';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { ITEM_FUNCTION_TPYE_VALUE } from '@typings/store/storeItemFunctionType';

const focusInput = css`
  /* border: 1px solid ${Color.GRAY_8}; */
`;

const Container = styled.div<{
  disabled?: boolean;
  block?: boolean;
  isFocus?: boolean;
}>`
  position: relative;
  min-height: 40px;
  height: auto;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px 8px;
  border-radius: var(--cardBorderRadius);
  outline: none;
  background-color: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);

  ${({ isFocus }) => isFocus && focusInput}
  gap: 8px;

  input {
    flex: 1;
    width: 100%;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding-bottom: 8px;
`;

interface Props {
  collectionPath: string;
  setSelectedIDList: any;
  selectedIDList: string[];
  items?: any[]; // 외부에서 전달한 아이템 목록 (데이터 소스 대체용)
  onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  maxLength?: number;
  maxHashtagLength?: number;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  block?: boolean;
  id: string;
  selectOnlyOne?: boolean;
  dir?: keyof typeof dropdownDirMap;
  customDelete?: (index) => void;
  required?: boolean;
  orderBy?: [string, 'asc' | 'desc'];
  labelAlign?: 'left' | 'center' | 'right';
  align?: 'left' | 'center' | 'right';
  direction?: 'row' | 'column';
  labelWidth?: string | number;
  myInventory?: boolean; // 대표 캐릭터의 인벤토리 아이템만 표시
  itemTypeListFilter?: ITEM_FUNCTION_TPYE_VALUE[]; // 특정 아이템 타입만 필터링
  inputStyle?: CSSProperties;
}

function ItemSelector({
  maxLength,
  setSelectedIDList,
  selectedIDList,
  maxHashtagLength = 10,
  label,
  collectionPath,
  disabled,
  onChange,
  items,
  placeholder = '아이템명 입력 후 ENTER',
  block,
  id,
  selectOnlyOne = false,
  dir,
  customDelete,
  required = false,
  orderBy,
  labelAlign = 'left',
  direction = 'column',
  labelWidth,
  myInventory = false,
  itemTypeListFilter = [],
  align,
  inputStyle,
}: Props) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const handleFocus = useCallback(() => setIsFocus(true), []);
  const handleBlur = useCallback(() => setIsFocus(false), []);
  const [value, setValue] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // 대표 캐릭터 정보
  const representativeCharacter = useRecoilValue(mainPhotocardDataState);

  // 기본 컬렉션 데이터
  const { data } = useCollection<any>(items ? null : `${collectionPath}`, {
    ...(orderBy ? { orderBy } : {}),
    listen: true,
  });

  // 대표 캐릭터의 인벤토리 데이터
  const { data: inventoryList } = useCollection<IinventoryItem>(
    myInventory && representativeCharacter?.id
      ? `${CollectionPath.PHOTOCARD}/${representativeCharacter.id}/inventory`
      : null,
    { listen: true, orderBy: ['createdAt', 'desc'] },
  );

  // Store 아이템 전체 (인벤토리 매핑용)
  const { data: storeItems } = useCollection<IstoreItem>(
    myInventory ? CollectionPath.STORE : null,
    { listen: true },
  );

  const [filteredList, setFilteredList] = useState<Ihashtag[] | null>([]);
  const [selectedItemsData, setSelectedItemsData] = useState<Map<string, any>>(
    new Map(),
  );

  // itemTypeListFilter를 문자열로 안정화 (배열 참조 문제 해결)
  const itemTypeFilterKey = useMemo(
    () => itemTypeListFilter.sort().join(','),
    [itemTypeListFilter.join(',')],
  );

  // 최종 데이터 소스 결정
  const dataSource = useMemo(() => {
    // 외부에서 items를 전달한 경우 우선 사용
    if (items) return items;

    // myInventory 옵션이 활성화된 경우
    if (myInventory) {
      if (!inventoryList) return [];

      // 인벤토리 아이템을 기반으로 표시 목록 생성
      const inventoryMap = new Map();
      const storeMap = new Map(
        storeItems?.map((item) => [item.id, item]) || [],
      );

      inventoryList.forEach((invItem) => {
        const itemId = invItem.originItemID || invItem.id;
        if (!itemId) return;

        // 이미 처리된 아이템이면 스킵 (중복 제거)
        if (inventoryMap.has(itemId)) return;

        // Store에 원본이 있으면 Store 데이터 사용
        const storeData = storeMap.get(itemId);

        // 없으면 인벤토리 데이터를 변환하여 사용
        const displayItem = storeData || {
          id: itemId, // id는 명확하게 itemId로 설정
          title: invItem.title || itemId,
          description: invItem.description || '',
          imgURL: invItem.imgURL || '',
          functionType: invItem.functionType,
          // 기타 필요한 필드들
          order: invItem.order || 0,
          price: invItem.price || '0',
          isShow: true,
        };

        // itemTypeListFilter가 있으면 필터링
        if (itemTypeListFilter.length > 0) {
          if (
            !displayItem.functionType ||
            !itemTypeListFilter.includes(displayItem.functionType)
          ) {
            return;
          }
        }

        inventoryMap.set(itemId, displayItem);
      });

      return Array.from(inventoryMap.values());
    }

    // 기본: 컬렉션 데이터 사용
    return data ?? [];
  }, [items, myInventory, inventoryList, storeItems, data, itemTypeFilterKey]);

  const handleChange = useCallback(
    (e: any) => {
      if (onChange) onChange(e);
      setValue(e.target.value);
    },
    [onChange],
  );

  // 드롭다운 바깥 클릭
  function handleClickOutside(e: any, dropdownNode) {
    if (targetRef.current && isFocus && dropdownNode) {
      const isClickOutside =
        !targetRef.current.contains(e.target) &&
        !dropdownNode.contains(e.target);
      if (isClickOutside) handleBlur();
    }
  }

  // 삭제
  const HandleRemoveValue = (index: number) => {
    if (customDelete) customDelete(index);

    // 선택된 아이템 데이터도 함께 삭제
    const removedId = selectedIDList[index];
    if (myInventory && removedId) {
      setSelectedItemsData((prev) => {
        const newMap = new Map(prev);
        newMap.delete(removedId);
        return newMap;
      });
    }

    setSelectedIDList((prev) => {
      const copyList = [...prev];
      copyList.splice(index, 1);
      return [...copyList];
    });
  };

  // enter 또는 click 했을 때 적용됨
  const handleSelect = useCallback(
    (index: number) => {
      if (filteredList && filteredList[index]) {
        const selectedItem = filteredList[index];
        const selectedId = selectedItem.id;

        if (selectedId) {
          // 선택된 아이템 데이터 저장 (myInventory 모드에서 사용)
          if (myInventory) {
            setSelectedItemsData((prev) => {
              const newMap = new Map(prev);
              newMap.set(selectedId, selectedItem);
              return newMap;
            });
          }

          // 새로운 리스트 계산
          const newList = selectOnlyOne
            ? [selectedId]
            : [...selectedIDList, selectedId];

          setSelectedIDList(newList); // 배열을 직접 전달

          // 입력창 초기화는 약간 지연시켜서 상태 업데이트 후 실행
          setTimeout(() => setValue(''), 0);
        }
      }
    },
    [
      filteredList,
      selectedIDList,
      setSelectedIDList,
      selectOnlyOne,
      myInventory,
    ],
  );

  // 서치
  useEffect(() => {
    if (!dataSource || !value) return;
    const titleResult = filterTitleBySearchValue(dataSource, value);
    setFilteredList(titleResult);
  }, [value, dataSource]);

  useEffect(() => {
    if (!dataSource) return;
    setFilteredList(dataSource);
  }, [dataSource, selectedIDList, myInventory, itemTypeFilterKey]);

  const shouldShowComponent = selectOnlyOne
    ? !selectedIDList || selectedIDList.length === 0
    : true;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: direction || 'column',
        alignItems: align || 'flex-start',
        gap: '4px',
        width: '100%',
      }}
    >
      {label && (
        <LabelSet
          label={label}
          align={labelAlign}
          labelWidth={labelWidth}
          required={required}
        />
      )}
      {selectedIDList && selectedIDList.length > 0 && (
        <TagContainer>
          {(selectedIDList || []).map((item, i) => {
            // myInventory 모드에서 저장된 데이터가 있으면 전달
            // items prop이 있으면 해당 목록에서 데이터 찾기
            let itemData = myInventory ? selectedItemsData.get(item) : null;
            if (!itemData && items) {
              itemData = items.find((it) => it.id === item) || null;
            }

            return (
              <TagItem
                key={i}
                handleDelete={HandleRemoveValue}
                id={item}
                index={i}
                collectionPath={collectionPath}
                data={itemData}
              />
            );
          })}
        </TagContainer>
      )}
      {shouldShowComponent && (
        <Container
          key={id}
          disabled={disabled}
          isFocus={isFocus}
          ref={targetRef}
          style={inputStyle}
        >
          <input
            type="text"
            maxLength={maxLength}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
          />
          <AnimatePresence>
            {isFocus && dataSource && dataSource.length > 0 && (
              <DropdownWrapper
                open={isFocus}
                direction={dir || 'topLeft'}
                handleClickOutside={handleClickOutside}
                style={{ width: '100%' }}
              >
                <ResultDropdown
                  id={id}
                  isOpen={isFocus}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  filteredList={filteredList}
                  handleSelect={handleSelect}
                  maxHeight={200}
                  showDescription={false}
                />
              </DropdownWrapper>
            )}
          </AnimatePresence>
        </Container>
      )}
    </div>
  );
}

export default ItemSelector;
